import React, { useState } from 'react';
import { Box, Stack, Typography, Button } from '@mui/material';
import { FactCheckOutlined } from '@mui/icons-material';
import { SectionHeader, SectionPaper } from '../../../../common/components/styled-ui-elements';
import { useAppSelector } from '../../../../../app/hooks';
import { ComplianceCompleteDialog } from '../../ComplianceCompleteDialog';
import { ComplianceItemView } from '../../../../../services/ComplianceService/types/complianceTypes';

interface CompletionSummaryProps {
  completedBy?: string;
  completedDate?: string;
  isVendorPerformed?: boolean;
  vendorName?: string;
  verifiedBy?: string;
  verifiedDate?: string;
}

/**
 * Resolves the completion/verification fields off the current item in one place, so the section
 * component itself has no optional-chaining branches of its own.
 */
const resolveCompletionSummaryProps = (item: ComplianceItemView | null): CompletionSummaryProps => ({
  completedBy: item?.completedBy,
  completedDate: item?.completedDate,
  isVendorPerformed: item?.isVendorPerformed,
  vendorName: item?.vendorName,
  verifiedBy: item?.verifiedBy,
  verifiedDate: item?.verifiedDate,
});

/** The complete dialog only targets an item while it is open; otherwise there is nothing to complete. */
const resolveDialogTargetId = (dialogOpen: boolean, item: ComplianceItemView | null): string | null =>
  dialogOpen ? item?.id || null : null;

/** Renders the "not completed yet" placeholder, or the last completion/verification summary. */
const CompletionSummary: React.FC<CompletionSummaryProps> = ({
  completedBy,
  completedDate,
  isVendorPerformed,
  vendorName,
  verifiedBy,
  verifiedDate,
}) => {
  if (!completedDate) {
    return (
      <Typography variant="body2" color="#64748b">
        This requirement has not been completed yet.
      </Typography>
    );
  }

  const vendorSuffix = isVendorPerformed && vendorName ? ` (vendor: ${vendorName})` : '';

  return (
    <>
      <Typography variant="body2" color="#1e293b">
        Last completed by <strong>{completedBy}</strong> on {completedDate.slice(0, 10)}{vendorSuffix}
      </Typography>
      {verifiedBy && (
        <Typography variant="body2" color="#64748b">
          Verified by <strong>{verifiedBy}</strong> on {verifiedDate?.slice(0, 10)}
        </Typography>
      )}
    </>
  );
};

/**
 * CompletionTrackingSection - Shows the last completion/verification summary for a compliance
 * requirement and lets the coordinator mark it complete again (US4, T052). Read-only when there is
 * no recorded completion yet.
 */
export const CompletionTrackingSection: React.FC = () => {
  const { currentComplianceItem } = useAppSelector(state => state.compliance);
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <Box>
      <SectionHeader
        icon={<FactCheckOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />}
        title="Completion Tracking"
      />
      <SectionPaper>
        <Stack spacing={1.5}>
          <CompletionSummary {...resolveCompletionSummaryProps(currentComplianceItem)} />

          <Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDialogOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Mark Complete
            </Button>
          </Box>
        </Stack>
      </SectionPaper>

      <ComplianceCompleteDialog
        complianceItemId={resolveDialogTargetId(dialogOpen, currentComplianceItem)}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
};

export default CompletionTrackingSection;
